import axios from "axios";
import AuthService from "../utils/AuthService";

const ensureNoTrailingSlash = (value) => {
  if (!value) {
    return "";
  }

  return value.replace(/\/+$/, "");
};

const ensureLeadingSlash = (value) => {
  if (!value) {
    return "";
  }

  return value.startsWith("/") ? value : `/${value}`;
};

const getAssetsBaseUrl = () => {
  const configured =
    process.env.REACT_APP_ASSETS_API_URL || process.env.REACT_APP_ITEMS_API_URL;

  if (!configured) {
    throw new Error("Assets API URL is not configured.");
  }

  return ensureNoTrailingSlash(configured.trim());
};

const buildAssetsUrl = (path = "") => {
  const base = getAssetsBaseUrl();
  const sanitizedPath = ensureLeadingSlash(path).replace(/\/+/g, "/");

  return `${base}${sanitizedPath}`;
};

const toIsoString = (value) => {
  if (!value) {
    return null;
  }

  const date = value instanceof Date ? value : new Date(value);

  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return date.toISOString();
};

const normaliseAssetResponse = (response) => {
  if (!response) {
    return { items: [], totalCount: 0, nextToken: null };
  }

  const extract = (items, totalCount = undefined, nextToken = null) => {
    const parsedTotal = Number(totalCount);
    const hasValidTotal = Number.isFinite(parsedTotal) && parsedTotal >= 0;

    return {
      items: Array.isArray(items) ? items : [],
      totalCount:
        hasValidTotal
          ? parsedTotal
          : Array.isArray(items)
          ? items.length
          : 0,
      nextToken: nextToken || null,
    };
  };

  if (Array.isArray(response.items)) {
    return extract(response.items, response.totalCount, response.nextToken);
  }

  if (Array.isArray(response.results)) {
    return extract(response.results, response.totalCount, response.nextToken);
  }

  if (Array.isArray(response)) {
    return extract(response, response.totalCount, response.nextToken);
  }

  if (response.item) {
    return extract([response.item], 1, response.nextToken);
  }

  return extract([], response.totalCount, response.nextToken);
};

const AssetService = {
  async listAssets({
    search,
    tags,
    limit,
    nextToken,
    createdAfter,
    createdBefore,
    contentType,
  } = {}) {
    const params = new URLSearchParams();

    if (typeof limit === "number" && Number.isFinite(limit) && limit > 0) {
      params.set("limit", String(Math.floor(limit)));
    }

    const trimmedSearch = typeof search === "string" ? search.trim() : "";
    if (trimmedSearch) {
      params.set("fileName", trimmedSearch);
    }

    if (Array.isArray(tags) && tags.length > 0) {
      tags
        .filter((tag) => typeof tag === "string" && tag.trim().length > 0)
        .forEach((tag) => params.append("tag", tag));
    }

    if (nextToken) {
      params.set("nextToken", nextToken);
    }

    const createdAfterIso = toIsoString(createdAfter);
    if (createdAfterIso) {
      params.set("createdAfter", createdAfterIso);
    }

    const createdBeforeIso = toIsoString(createdBefore);
    if (createdBeforeIso) {
      params.set("createdBefore", createdBeforeIso);
    }

    if (contentType) {
      params.set("contentType", contentType);
    }

    const query = params.toString();
    const url = buildAssetsUrl(`/assets${query ? `?${query}` : ""}`);

    const response = await axios.get(url, AuthService.getAuthHeader());

    return normaliseAssetResponse(response.data);
  },

  async createAsset(payload) {
    if (!payload || typeof payload !== "object") {
      throw new Error("A payload is required to create an asset.");
    }

    const url = buildAssetsUrl("/asset");
    const response = await axios.post(url, payload, AuthService.getAuthHeader());
    return response.data;
  },

  async uploadAsset(uploadUrl, file, { onUploadProgress } = {}) {
    if (!uploadUrl) {
      throw new Error("An upload URL is required to upload an asset.");
    }

    if (!file) {
      throw new Error("A file is required to upload an asset.");
    }

    await axios.put(uploadUrl, file, {
      headers: {
        "Content-Type": file.type || "application/octet-stream",
      },
      onUploadProgress,
    });
  },

  async updateAsset(assetId, payload) {
    if (!assetId) {
      throw new Error("An asset id is required to update an asset.");
    }

    const url = buildAssetsUrl(`/asset/${encodeURIComponent(assetId)}`);
    const response = await axios.put(url, payload, AuthService.getAuthHeader());
    return response.data;
  },

  async deleteAsset(assetId) {
    if (!assetId) {
      throw new Error("An asset id is required to delete an asset.");
    }

    const url = buildAssetsUrl(`/asset/${encodeURIComponent(assetId)}`);
    await axios.delete(url, AuthService.getAuthHeader());
  },

  async fetchAssetUsage(assetId) {
    if (!assetId) {
      throw new Error("An asset id is required to fetch usage details.");
    }

    const url = buildAssetsUrl(
      `/asset/${encodeURIComponent(assetId)}/references`
    );
    const response = await axios.get(url, AuthService.getAuthHeader());
    const data = response.data || {};

    return {
      items: Array.isArray(data.items) ? data.items : [],
      lessons: Array.isArray(data.lessons) ? data.lessons : [],
    };
  },
};

export default AssetService;
